import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  LifeBuoy, MessageCircle, BookOpen, Ticket, ChevronDown, ChevronUp,
  Search, Send, CheckCircle2, Clock, AlertCircle, ArrowRight,
  Phone, Mail, FileText, Zap, Star, ThumbsUp, ThumbsDown,
  ExternalLink, PlusCircle, X
} from "lucide-react";
import { Link } from "react-router-dom";

// ─── Data ────────────────────────────────────────────────────────────────────

const categories = [
  { label: "Getting Started", icon: Zap, color: "text-primary", bg: "bg-primary/10", count: 12 },
  { label: "Account & Billing", icon: FileText, color: "text-warning", bg: "bg-warning/10", count: 8 },
  { label: "Courses & Content", icon: BookOpen, color: "text-success", bg: "bg-success/10", count: 21 },
  { label: "Technical Issues", icon: AlertCircle, color: "text-destructive", bg: "bg-destructive/10", count: 15 },
];

const faqs = [
  {
    id: 1,
    category: "Getting Started",
    question: "How do I create my first course?",
    answer: "Navigate to the Courses section from your dashboard, then click '+ New Course'. Fill in the course details including title, description, and category. You can then add modules, lessons, quizzes, and assignments. Once ready, publish it to make it visible to enrolled students."
  },
  {
    id: 2,
    category: "Courses & Content",
    question: "How do I add students to my course?",
    answer: "Go to your course settings and select the 'Students' tab. You can invite students via email, share an enrollment link, or import a CSV of student emails. Students will receive an invitation and can join with one click."
  },
  {
    id: 3,
    category: "Technical Issues",
    question: "Why aren't my videos uploading?",
    answer: "Video uploads support MP4, MOV, and AVI formats up to 2GB per file. Ensure your internet connection is stable. If the issue persists, try a different browser or clear your cache. For large files, use the desktop uploader app for more reliability."
  },
  {
    id: 4,
    category: "Account & Billing",
    question: "How do I upgrade my plan?",
    answer: "Go to Settings → Billing → Upgrade Plan. Choose from Pro, Team, or Enterprise tiers. Changes take effect immediately and you'll be charged a prorated amount for the remainder of the billing cycle."
  },
  {
    id: 5,
    category: "Courses & Content",
    question: "Can I duplicate an existing course?",
    answer: "Yes! Open the course you want to duplicate, click the three-dot menu in the top right, and select 'Duplicate Course'. A copy will be created with all modules and content, marked as a draft so you can edit before publishing."
  },
  {
    id: 6,
    category: "Getting Started",
    question: "How do I track student progress?",
    answer: "The Assessment Center → Student Progress section gives you a full breakdown per student: completion rate, quiz scores, assignment submissions, and engagement trends. You can also export reports as PDF or CSV."
  },
];

const tickets = [
  {
    id: "TKT-1042",
    subject: "Quiz timer not working on mobile",
    status: "open",
    priority: "high",
    created: "2 hours ago",
    lastUpdate: "1 hour ago",
    category: "Technical Issues"
  },
  {
    id: "TKT-1038",
    subject: "Unable to export student report as PDF",
    status: "in-progress",
    priority: "medium",
    created: "1 day ago",
    lastUpdate: "3 hours ago",
    category: "Courses & Content"
  },
  {
    id: "TKT-1031",
    subject: "Billing invoice missing for March",
    status: "resolved",
    priority: "low",
    created: "5 days ago",
    lastUpdate: "2 days ago",
    category: "Account & Billing"
  },
  {
    id: "TKT-1024",
    subject: "Request to increase video upload limit",
    status: "resolved",
    priority: "low",
    created: "2 weeks ago",
    lastUpdate: "1 week ago",
    category: "Technical Issues"
  },
];

const statusConfig = {
  open: { label: "Open", cls: "bg-amber-100 text-amber-700", icon: Clock },
  "in-progress": { label: "In Progress", cls: "bg-sky-100 text-sky-700", icon: Zap },
  resolved: { label: "Resolved", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};

const priorityConfig = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-slate-100 text-slate-600",
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  const [voted, setVoted] = useState(null);

  return (
    <div className="border rounded-2xl overflow-hidden transition-all duration-200 hover:shadow-md bg-card">
      <button
        className="w-full flex items-center justify-between p-5 text-left gap-4"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-start gap-3 flex-1">
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground mt-0.5 whitespace-nowrap">
            {faq.category}
          </span>
          <span className="font-semibold text-base">{faq.question}</span>
        </div>
        {open
          ? <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          : <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
      </button>

      <div className={`transition-all duration-300 overflow-hidden ${open ? "max-h-96" : "max-h-0"}`}>
        <div className="px-5 pb-5 space-y-4 border-t pt-4">
          <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>Was this helpful?</span>
            <button
              onClick={() => setVoted("up")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors ${
                voted === "up" ? "bg-success/10 border-success/30 text-success" : "hover:bg-muted"
              }`}
            >
              <ThumbsUp className="h-3.5 w-3.5" /> Yes
            </button>
            <button
              onClick={() => setVoted("down")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors ${
                voted === "down" ? "bg-destructive/10 border-destructive/30 text-destructive" : "hover:bg-muted"
              }`}
            >
              <ThumbsDown className="h-3.5 w-3.5" /> No
            </button>
            {voted && <span className="text-xs text-success font-medium">Thanks for your feedback!</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewTicketModal({ onClose }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("medium");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!subject || !description) return;
    setSubmitted(true);
    setTimeout(onClose, 2000);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl text-center p-8">
          <CheckCircle2 className="h-16 w-16 text-success mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-2">Ticket Submitted!</h3>
          <p className="text-muted-foreground">Our support team will get back to you within 24 hours.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg border-0 shadow-2xl">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold">Submit a Support Ticket</CardTitle>
            <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-semibold mb-1.5 block">Subject *</label>
            <input
              className="w-full px-3 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Brief description of the issue"
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Category</label>
              <select
                className="w-full px-3 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={category}
                onChange={e => setCategory(e.target.value)}
              >
                <option value="">Select category</option>
                {categories.map(c => <option key={c.label} value={c.label}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Priority</label>
              <select
                className="w-full px-3 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={priority}
                onChange={e => setPriority(e.target.value)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold mb-1.5 block">Description *</label>
            <textarea
              className="w-full px-3 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              rows={4}
              placeholder="Describe your issue in detail. Include any error messages, steps to reproduce, and what you expected to happen."
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button
              className="flex-1 gradient-primary text-white"
              onClick={handleSubmit}
              disabled={!subject || !description}
            >
              <Send className="mr-2 h-4 w-4" /> Submit Ticket
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function SupportPage() {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("faq");
  const [faqCategory, setFaqCategory] = useState("All");
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([
    { from: "support", text: "Hi there! 👋 How can I help you today?" }
  ]);

  const tabs = [
    { id: "faq", label: "FAQ", icon: BookOpen },
    { id: "tickets", label: "My Tickets", icon: Ticket },
    { id: "contact", label: "Contact Us", icon: Mail },
  ];

  const filteredFaqs = faqs.filter(f => {
    const matchCat = faqCategory === "All" || f.category === faqCategory;
    const matchSearch = f.question.toLowerCase().includes(search.toLowerCase()) ||
      f.answer.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const openTickets = tickets.filter(t => t.status !== "resolved").length;

  const sendChat = () => {
    if (!chatMessage.trim()) return;
    setChatHistory(prev => [
      ...prev,
      { from: "user", text: chatMessage },
      { from: "support", text: "Thanks for your message! A support agent will respond shortly. Average response time is under 5 minutes." }
    ]);
    setChatMessage("");
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-4 gradient-primary rounded-2xl shadow-lg">
            <LifeBuoy className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight mb-1">Support Center</h1>
            <p className="text-muted-foreground text-lg">Find answers, manage tickets, and get help fast</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/">
            <Button variant="outline" size="lg" className="shadow-sm font-medium">← Dashboard</Button>
          </Link>
          <Button
            size="lg"
            className="gradient-primary text-white shadow-md font-semibold"
            onClick={() => setShowNewTicket(true)}
          >
            <PlusCircle className="mr-2 h-4 w-4" /> New Ticket
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-2xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input
          className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 bg-card shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
          placeholder="Search for answers, articles, or topics…"
          value={search}
          onChange={e => { setSearch(e.target.value); setActiveTab("faq"); }}
        />
      </div>

      {/* Quick Category Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {categories.map(cat => (
          <button
            key={cat.label}
            onClick={() => { setFaqCategory(cat.label); setActiveTab("faq"); setSearch(""); }}
            className={`group p-5 rounded-2xl border-2 text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 bg-card ${
              faqCategory === cat.label ? "border-primary/50 shadow-md" : "border-transparent"
            }`}
          >
            <div className={`inline-flex p-2.5 rounded-xl ${cat.bg} mb-3`}>
              <cat.icon className={`h-5 w-5 ${cat.color}`} />
            </div>
            <p className="font-semibold text-sm mb-1">{cat.label}</p>
            <p className="text-xs text-muted-foreground">{cat.count} articles</p>
          </button>
        ))}
      </div>

      {/* Status Banner */}
      <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 rounded-2xl">
        <div className="h-2.5 w-2.5 rounded-full bg-success animate-pulse" />
        <p className="text-sm font-medium text-success">All systems operational</p>
        <span className="text-muted-foreground text-sm mx-1">·</span>
        <p className="text-sm text-muted-foreground">Average response time today: <strong className="text-foreground">~4 minutes</strong></p>
        <a href="#" className="ml-auto text-xs text-primary flex items-center gap-1 hover:underline">
          Status page <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-2xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
              activeTab === tab.id
                ? "bg-background shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
            {tab.id === "tickets" && openTickets > 0 && (
              <span className="bg-primary text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                {openTickets}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── FAQ Tab ─────────────────────────────────────────────────────────── */}
      {activeTab === "faq" && (
        <div className="space-y-6">
          {/* Category filter pills */}
          <div className="flex gap-2 flex-wrap">
            {["All", ...categories.map(c => c.label)].map(cat => (
              <button
                key={cat}
                onClick={() => setFaqCategory(cat)}
                className={`text-sm px-4 py-1.5 rounded-full border font-medium transition-all ${
                  faqCategory === cat
                    ? "gradient-primary text-white border-transparent shadow-sm"
                    : "bg-card hover:bg-muted text-muted-foreground"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map(faq => <FAQItem key={faq.id} faq={faq} />)
            ) : (
              <div className="text-center py-16 text-muted-foreground">
                <Search className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="font-semibold text-lg mb-1">No results found</p>
                <p className="text-sm">Try different keywords or <button className="text-primary underline" onClick={() => setShowNewTicket(true)}>submit a ticket</button></p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tickets Tab ─────────────────────────────────────────────────────── */}
      {activeTab === "tickets" && (
        <div className="space-y-4">
          {/* Ticket stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Open", value: tickets.filter(t => t.status === "open").length, color: "text-amber-600", bg: "bg-amber-50" },
              { label: "In Progress", value: tickets.filter(t => t.status === "in-progress").length, color: "text-sky-600", bg: "bg-sky-50" },
              { label: "Resolved", value: tickets.filter(t => t.status === "resolved").length, color: "text-emerald-600", bg: "bg-emerald-50" },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-2xl p-4 text-center`}>
                <p className={`text-3xl font-extrabold ${s.color}`}>{s.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Ticket list */}
          <div className="space-y-3">
            {tickets.map(ticket => {
              const StatusIcon = statusConfig[ticket.status].icon;
              return (
                <Card key={ticket.id} className="border-0 shadow-md hover:shadow-lg transition-all duration-200">
                  <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-mono font-bold text-muted-foreground">{ticket.id}</span>
                          <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${statusConfig[ticket.status].cls}`}>
                            <StatusIcon className="h-3 w-3" /> {statusConfig[ticket.status].label}
                          </span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${priorityConfig[ticket.priority]}`}>
                            {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)} priority
                          </span>
                        </div>
                        <h3 className="font-bold text-base">{ticket.subject}</h3>
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" />{ticket.category}</span>
                          <span className="flex items-center gap-1"><PlusCircle className="h-3 w-3" />Created {ticket.created}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Updated {ticket.lastUpdate}</span>
                        </div>
                      </div>
                      <Button size="sm" variant={ticket.status === "resolved" ? "outline" : "default"}
                        className={ticket.status !== "resolved" ? "gradient-primary text-white text-xs" : "text-xs"}>
                        {ticket.status === "resolved" ? "View" : "View & Reply"} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Button
            className="w-full gradient-primary text-white font-semibold h-12"
            onClick={() => setShowNewTicket(true)}
          >
            <PlusCircle className="mr-2 h-5 w-5" /> Submit a New Ticket
          </Button>
        </div>
      )}

      {/* ── Contact Tab ─────────────────────────────────────────────────────── */}
      {activeTab === "contact" && (
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: MessageCircle,
              title: "Live Chat",
              desc: "Chat with a support agent in real time. Available Mon–Fri, 9am–6pm WAT.",
              action: "Start Chat",
              gradient: "gradient-primary",
              badge: "~4 min response",
              badgeCls: "bg-success/10 text-success",
              onClick: () => setChatOpen(true)
            },
            {
              icon: Mail,
              title: "Email Support",
              desc: "Send us a detailed message and we'll get back to you within 24 hours.",
              action: "Send Email",
              gradient: "gradient-info",
              badge: "< 24hr response",
              badgeCls: "bg-info/10 text-info",
              onClick: () => setShowNewTicket(true)
            },
            {
              icon: Phone,
              title: "Phone Support",
              desc: "Speak directly with our technical support team for urgent issues.",
              action: "Call +234 800 FILLOP",
              gradient: "gradient-success",
              badge: "Enterprise only",
              badgeCls: "bg-muted text-muted-foreground",
              onClick: () => {}
            }
          ].map(channel => (
            <Card key={channel.title} className="border-0 shadow-md hover:shadow-xl transition-all duration-200 group">
              <CardContent className="p-6 space-y-4">
                <div className={`inline-flex p-3 ${channel.gradient} rounded-2xl shadow-md`}>
                  <channel.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-lg">{channel.title}</h3>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${channel.badgeCls}`}>
                      {channel.badge}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{channel.desc}</p>
                </div>
                <Button
                  className={`w-full ${channel.gradient} text-white font-semibold`}
                  onClick={channel.onClick}
                >
                  {channel.action} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}

          {/* Resources */}
          <Card className="border-0 shadow-md md:col-span-3">
            <CardHeader>
              <CardTitle className="text-lg font-bold">Helpful Resources</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-3">
                {[
                  { title: "Documentation", desc: "Full platform guides and API docs", icon: FileText },
                  { title: "Video Tutorials", desc: "Step-by-step walkthroughs", icon: Star },
                  { title: "Community Forum", desc: "Ask questions, share ideas", icon: MessageCircle },
                ].map(r => (
                  <div key={r.title} className="flex items-center gap-3 p-4 rounded-xl bg-muted/40 hover:bg-muted/70 transition-colors cursor-pointer group">
                    <div className="p-2 bg-background rounded-xl shadow-sm">
                      <r.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm group-hover:text-primary transition-colors">{r.title}</p>
                      <p className="text-xs text-muted-foreground">{r.desc}</p>
                    </div>
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── New Ticket Modal ─────────────────────────────────────────────────── */}
      {showNewTicket && <NewTicketModal onClose={() => setShowNewTicket(false)} />}

      {/* ── Live Chat Widget ─────────────────────────────────────────────────── */}
      {chatOpen && (
        <div className="fixed bottom-6 right-6 w-80 z-50">
          <Card className="border-0 shadow-2xl overflow-hidden">
            {/* Chat Header */}
            <div className="gradient-primary p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
                  <LifeBuoy className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Fillop Support</p>
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-300 animate-pulse" />
                    <p className="text-white/80 text-xs">Online now</p>
                  </div>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} className="text-white/70 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="h-56 overflow-y-auto p-4 space-y-3 bg-muted/20">
              {chatHistory.map((msg, i) => (
                <div key={i} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                    msg.from === "user"
                      ? "gradient-primary text-white rounded-br-sm"
                      : "bg-card border rounded-bl-sm"
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="p-3 border-t flex gap-2">
              <input
                className="flex-1 px-3 py-2 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Type a message…"
                value={chatMessage}
                onChange={e => setChatMessage(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendChat()}
              />
              <button
                onClick={sendChat}
                className="p-2 gradient-primary rounded-xl text-white shadow-sm hover:shadow-md transition-shadow"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}